import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Trash2, Calendar, FileText, Award, Edit, GripVertical } from 'lucide-react';
import { db, auth } from '../../lib/firebase';
import { collection, getDocs, addDoc, deleteDoc, doc, query, orderBy, updateDoc, writeBatch } from 'firebase/firestore';

export default function AdminHistory() {
  const navigate = useNavigate();
  const [histories, setHistories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [newHistory, setNewHistory] = useState({ year: new Date().getFullYear(), project: '', description: '' });
  const [editingId, setEditingId] = useState(null);
  const [editingHistory, setEditingHistory] = useState({ year: '', project: '', description: '' });
  const [draggedItem, setDraggedItem] = useState(null);

  // Check authentication on component mount
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (!user) {
        navigate('/admin/login');
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  // Fetch history data from Firebase
  useEffect(() => {
    const fetchHistory = async () => {
      try {
        setIsLoading(true);
        const historyQuery = query(collection(db, 'histories'), orderBy('order', 'asc'));
        const historySnapshot = await getDocs(historyQuery);
        const historyData = historySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setHistories(historyData);
      } catch (err) {
        console.error('History fetch failed:', err);
        alert('연혁 데이터를 불러오는 중 오류가 발생했습니다.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchHistory();
  }, []);

  const handleAddHistory = async () => {
    if (!newHistory.year || !newHistory.project || !newHistory.description) {
      alert('모든 필드를 입력해주세요.');
      return;
    }

    try {
      const docRef = await addDoc(collection(db, 'histories'), {
        ...newHistory,
        order: histories.length > 0 ? Math.max(...histories.map(h => h.order || 0)) + 1 : 1
      });
      setHistories(prev => [...prev, { 
        id: docRef.id, 
        ...newHistory,
        order: histories.length > 0 ? Math.max(...histories.map(h => h.order || 0)) + 1 : 1
      }]);
      setNewHistory({ year: new Date().getFullYear(), project: '', description: '' });
      alert('연혁이 추가되었습니다.');
    } catch (err) {
      console.error('History add failed:', err);
      alert('연혁 추가 중 오류가 발생했습니다.');
    }
  };

  const handleDeleteHistory = async (id) => {
    if (!window.confirm('정말 삭제하시겠습니까?')) return;

    try {
      await deleteDoc(doc(db, 'histories', id));
      setHistories(prev => prev.filter(history => history.id !== id));
      alert('연혁이 삭제되었습니다.');
    } catch (err) {
      console.error('History delete failed:', err);
      alert('연혁 삭제 중 오류가 발생했습니다.');
    }
  };

  const startEditing = (history) => {
    setEditingId(history.id);
    setEditingHistory({
      year: history.year,
      project: history.project,
      description: history.description
    });
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditingHistory({ year: '', project: '', description: '' });
  };

  const handleUpdateHistory = async () => {
    if (!editingHistory.year || !editingHistory.project || !editingHistory.description) {
      alert('모든 필드를 입력해주세요.');
      return;
    }

    try {
      await updateDoc(doc(db, 'histories', editingId), editingHistory);
      setHistories(prev => prev.map(history => 
        history.id === editingId 
          ? { ...history, ...editingHistory }
          : history
      ));
      setEditingId(null);
      setEditingHistory({ year: '', project: '', description: '' });
      alert('연혁이 수정되었습니다.');
    } catch (err) {
      console.error('History update failed:', err);
      alert('연혁 수정 중 오류가 발생했습니다.');
    }
  };

  const handleDragStart = (e, index) => {
    setDraggedItem(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, dropIndex) => {
    e.preventDefault();
    
    if (draggedItem === null || draggedItem === dropIndex) return;

    const newHistories = [...histories];
    const draggedItemData = newHistories[draggedItem];
    
    // Remove dragged item
    newHistories.splice(draggedItem, 1);
    // Insert at new position
    newHistories.splice(dropIndex, 0, draggedItemData);

    // Update order values
    const updatedHistories = newHistories.map((history, index) => ({
      ...history,
      order: index + 1
    }));

    setHistories(updatedHistories);

    try {
      // Batch update all orders
      const batch = writeBatch(db);
      updatedHistories.forEach((history) => {
        const docRef = doc(db, 'histories', history.id);
        batch.update(docRef, { order: history.order });
      });
      await batch.commit();
    } catch (err) {
      console.error('Order update failed:', err);
      alert('순서 업데이트 중 오류가 발생했습니다.');
      // Revert to original order on error
      const originalHistories = [...histories].sort((a, b) => (a.order || 0) - (b.order || 0));
      setHistories(originalHistories);
    }

    setDraggedItem(null);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">연혁 관리</h1>
          <p className="text-sm text-gray-600 mt-1">연도, 프로젝트명, 설명을 관리합니다</p>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-6">새 연혁 추가</h2>
        
        <div className="grid grid-cols-12 gap-4 mb-4">
          <div className="col-span-2">
            <input
              type="number"
              value={newHistory.year}
              onChange={(e) => setNewHistory(prev => ({ ...prev, year: parseInt(e.target.value) || '' }))}
              placeholder="연도"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            />
          </div>
          <div className="col-span-3">
            <input
              type="text"
              value={newHistory.project}
              onChange={(e) => setNewHistory(prev => ({ ...prev, project: e.target.value }))}
              placeholder="프로젝트명"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            />
          </div>
          <div className="col-span-6">
            <input
              type="text"
              value={newHistory.description}
              onChange={(e) => setNewHistory(prev => ({ ...prev, description: e.target.value }))}
              placeholder="설명"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            />
          </div>
          <div className="col-span-1">
            <button
              onClick={handleAddHistory}
              className="w-full h-10 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center justify-center"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-6">연혁 목록 (드래그하여 순서 변경)</h2>
        
        <div className="space-y-3">
          {histories.map((history, index) => (
            <div 
              key={history.id} 
              className="p-4 border border-gray-200 rounded-md hover:bg-gray-50 cursor-move"
              draggable
              onDragStart={(e) => handleDragStart(e, index)}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, index)}
            >
              {editingId === history.id ? (
                // 수정 모드
                <div className="grid grid-cols-12 gap-3">
                  <div className="col-span-2">
                    <input
                      type="number"
                      value={editingHistory.year}
                      onChange={(e) => setEditingHistory(prev => ({ ...prev, year: parseInt(e.target.value) || '' }))}
                      className="w-full px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    />
                  </div>
                  <div className="col-span-3">
                    <input
                      type="text"
                      value={editingHistory.project}
                      onChange={(e) => setEditingHistory(prev => ({ ...prev, project: e.target.value }))}
                      className="w-full px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    />
                  </div>
                  <div className="col-span-5">
                    <input
                      type="text"
                      value={editingHistory.description}
                      onChange={(e) => setEditingHistory(prev => ({ ...prev, description: e.target.value }))}
                      className="w-full px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    />
                  </div>
                  <div className="col-span-2 flex space-x-1">
                    <button
                      onClick={handleUpdateHistory}
                      className="flex-1 px-2 py-1 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm"
                    >
                      저장
                    </button>
                    <button
                      onClick={cancelEditing}
                      className="flex-1 px-2 py-1 bg-gray-400 text-white rounded-md hover:bg-gray-500 text-sm"
                    >
                      취소
                    </button>
                  </div>
                </div>
              ) : (
                // 보기 모드
                <div className="grid grid-cols-12 gap-3 items-center">
                  <div className="col-span-1 flex items-center justify-center text-gray-400">
                    <GripVertical className="w-4 h-4" />
                  </div>
                  <div className="col-span-2">
                    <div className="flex items-center space-x-2">
                      <Calendar className="w-4 h-4 text-gray-400" />
                      <span className="text-sm font-medium text-gray-900">{history.year}</span>
                    </div>
                  </div>
                  <div className="col-span-3">
                    <div className="flex items-center space-x-2">
                      <FileText className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-600">{history.project}</span>
                    </div>
                  </div>
                  <div className="col-span-4">
                    <div className="flex items-center space-x-2">
                      <Award className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-600">{history.description}</span>
                    </div>
                  </div>
                  <div className="col-span-2 flex justify-end space-x-2">
                    <button
                      onClick={() => startEditing(history)}
                      className="p-1 text-blue-600 hover:bg-blue-50 rounded-md"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteHistory(history.id)}
                      className="p-1 text-red-600 hover:bg-red-50 rounded-md"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
          
          {histories.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <FileText className="w-8 h-8 mx-auto mb-2 text-gray-300" />
              <p className="text-sm">등록된 연혁이 없습니다</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
